import * as React from "react";

export type Theme = "system" | "light" | "dark";

const STORAGE_KEY = "kb-theme";

function readStoredTheme(): Theme {
	if (typeof window === "undefined") {
		return "system";
	}

	const stored = window.localStorage.getItem(STORAGE_KEY);

	return stored === "light" || stored === "dark" || stored === "system"
		? stored
		: "system";
}

export function useTheme() {
	const [theme, setThemeState] = React.useState<Theme>(readStoredTheme);
	const hostTheme = React.useRef<string | null>(null);

	React.useEffect(() => {
		hostTheme.current = document.documentElement.getAttribute("data-theme");
	}, []);

	const apply = React.useCallback((next: Theme) => {
		const root = document.documentElement;

		root.classList.remove("light", "dark");

		if (next === "system") {
			if (hostTheme.current) {
				root.setAttribute("data-theme", hostTheme.current);
			} else {
				root.removeAttribute("data-theme");
			}

			return;
		}

		root.setAttribute("data-theme", next);
		root.classList.add(next);
	}, []);

	React.useEffect(() => {
		apply(theme);
	}, [apply, theme]);

	const setTheme = React.useCallback((next: Theme) => {
		window.localStorage.setItem(STORAGE_KEY, next);
		setThemeState(next);
	}, []);

	const cycleTheme = React.useCallback(() => {
		setTheme(
			theme === "system" ? "light" : theme === "light" ? "dark" : "system",
		);
	}, [setTheme, theme]);

	return { setTheme, theme, cycleTheme };
}
